import React, { useContext, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import { Button, TextField, Typography } from "@mui/material";

import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";

import Spinner from "../../Components/Spinner/Spinner";
import { AuthContext } from "../../Types/AuthContext";
import WorkDayRepository from "../../Repositories/WorkDayRepository";

const ACCENT = "#c79656";
const BORDER = "rgba(0,0,0,0.08)";

const csvImportMonthLookup: Record<string, number> = {
    januari: 1,
    january: 1,
    februari: 2,
    february: 2,
    maart: 3,
    march: 3,
    april: 4,
    mei: 5,
    may: 5,
    juni: 6,
    june: 6,
    juli: 7,
    july: 7,
    augustus: 8,
    august: 8,
    september: 9,
    oktober: 10,
    october: 10,
    november: 11,
    december: 12,
};

function cleanCsvCell(value: string): string {
    return value.trim().replace(/\ufeff/g, "").trim();
}

function parseCsvRows(csvContent: string): string[][] {
    const rows: string[][] = [];
    let currentRow: string[] = [];
    let currentCell = "";
    let inQuotes = false;

    for (let index = 0; index < csvContent.length; index++) {
        const currentChar = csvContent[index];

        if (currentChar === "\"") {
            const nextCharIsQuote = inQuotes && index + 1 < csvContent.length && csvContent[index + 1] === "\"";
            if (nextCharIsQuote) {
                currentCell += "\"";
                index++;
                continue;
            }

            inQuotes = !inQuotes;
            continue;
        }

        if (currentChar === "," && !inQuotes) {
            currentRow.push(currentCell);
            currentCell = "";
            continue;
        }

        if ((currentChar === "\r" || currentChar === "\n") && !inQuotes) {
            if (currentChar === "\r" && index + 1 < csvContent.length && csvContent[index + 1] === "\n") {
                index++;
            }

            currentRow.push(currentCell);
            currentCell = "";

            rows.push(currentRow);
            currentRow = [];
            continue;
        }

        currentCell += currentChar;
    }

    if (inQuotes) {
        throw new Error("CSV contains an unterminated quoted field.");
    }

    if (currentCell.length !== 0 || currentRow.length !== 0) {
        currentRow.push(currentCell);
        rows.push(currentRow);
    }

    return rows;
}

function getImportedMonth(csvContent: string, year: number): Dayjs {
    const rows = parseCsvRows(csvContent);
    if (rows.length < 4) {
        throw new Error("CSV must contain at least four rows.");
    }

    const monthName = cleanCsvCell(rows[3]?.[0] ?? "");
    const monthNumber = csvImportMonthLookup[monthName.toLowerCase()];

    if (!monthNumber) {
        throw new Error(`Unknown month '${monthName}'.`);
    }

    return dayjs(new Date(year, monthNumber - 1, 1)).startOf("month");
}

function parseYear(value: string): number | null {
    const normalized = value.trim();
    if (!/^\d{4}$/.test(normalized)) {
        return null;
    }

    const year = Number(normalized);
    return year >= 2000 && year <= 3000 ? year : null;
}

function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ImportCsvPage() {
    const navigate = useNavigate();
    const auth = useContext(AuthContext);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [file, setFile] = useState<File | null>(null);
    const [year, setYear] = useState(String(dayjs().year()));
    const [touched, setTouched] = useState(false);
    const [isDragActive, setIsDragActive] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [importedMonth, setImportedMonth] = useState<Dayjs | null>(null);

    const parsedYear = parseYear(year);
    const showYearError = touched && parsedYear === null;
    const showFileError = touched && file === null;
    const canSubmit = parsedYear !== null && file !== null && !isLoading;

    const acceptFile = (candidate: File | null | undefined): void => {
        if (!candidate) return;

        if (!candidate.name.toLowerCase().endsWith(".csv")) {
            setErrorMessage(`'${candidate.name}' is not a CSV file. Select a file that ends in .csv.`);
            return;
        }

        setFile(candidate);
        setErrorMessage("");
        setTouched(false);
    };

    const onImport = async (): Promise<void> => {
        if (!parsedYear || !file) {
            setTouched(true);
            return;
        }

        let csvContent = "";
        let month: Dayjs;

        try {
            csvContent = await file.text();
            month = getImportedMonth(csvContent, parsedYear);
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : "The CSV file could not be read.");
            return;
        }

        setErrorMessage("");
        setIsLoading(true);

        const result = await WorkDayRepository.importCsv(auth.locationId, csvContent, parsedYear);
        setIsLoading(false);

        if (result.isFailed() || !result.result) {
            setErrorMessage(result.errorMessage ?? "Something went wrong while importing the CSV file.");
            return;
        }

        setImportedMonth(month);
    };

    const resetForm = (): void => {
        setFile(null);
        setTouched(false);
        setErrorMessage("");
        setImportedMonth(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const zoneCaption = (label: string) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 13, opacity: 0.9 }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: ACCENT, display: "inline-block" }} />
            {label}
        </div>
    );

    return (
        <div style={{ width: "100%", height: "100%", boxSizing: "border-box", padding: 16, overflow: "auto" }}>
            {isLoading && <Spinner />}

            <div style={{ width: "100%", maxWidth: 760, margin: "0 auto", display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 6, height: 34, background: ACCENT, borderRadius: 6 }} />
                    <div style={{ fontSize: 26, fontWeight: 800, lineHeight: 1.1, color: "#1a1a1a", letterSpacing: "0.2px" }}>
                        Import CSV
                    </div>
                </div>

                <div
                    style={{
                        background: "#FFFFFF",
                        borderRadius: 14,
                        boxShadow: "0 6px 18px rgba(0,0,0,0.05)",
                        border: `1px solid ${BORDER}`,
                        padding: 20,
                        boxSizing: "border-box",
                        display: "flex",
                        flexDirection: "column",
                        gap: 16
                    }}
                >
                    {importedMonth ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "center", padding: "24px 0" }}>
                            <div
                                style={{
                                    width: 56,
                                    height: 56,
                                    borderRadius: 18,
                                    background: "#dcfce7",
                                    border: "1px solid #86efac",
                                    display: "grid",
                                    placeItems: "center",
                                    color: "#166534"
                                }}
                            >
                                <CheckCircleRoundedIcon sx={{ fontSize: 32 }} />
                            </div>

                            <div style={{ textAlign: "center" }}>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: "#1a1a1a", lineHeight: 1.2 }}>
                                    {importedMonth.format("MMMM YYYY")} imported
                                </Typography>
                                <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                                    The work days from the CSV file were added successfully.
                                </Typography>
                            </div>

                            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
                                <Button
                                    variant="contained"
                                    endIcon={<ArrowForwardRoundedIcon />}
                                    onClick={() => navigate(`/counting/manual?month=${importedMonth.format("YYYY-MM")}`)}
                                    sx={{
                                        textTransform: "none",
                                        fontWeight: 700,
                                        borderRadius: 999,
                                        px: 2.5,
                                        backgroundColor: ACCENT,
                                        "&:hover": { backgroundColor: "#b78445" },
                                        boxShadow: "none"
                                    }}
                                >
                                    Open in manual count
                                </Button>

                                <Button
                                    variant="outlined"
                                    onClick={resetForm}
                                    sx={{
                                        textTransform: "none",
                                        fontWeight: 700,
                                        borderRadius: 999,
                                        px: 2.5,
                                        borderColor: `${ACCENT}55`,
                                        color: "#1a1a1a"
                                    }}
                                >
                                    Import another file
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                {zoneCaption("Import work days")}
                                <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.5 }}>
                                    Upload a monthly work day export. The month is read from the file itself; only the year has to
                                    be chosen here.
                                </Typography>
                            </div>

                            <div style={{ display: "flex", flexDirection: "column", gap: 6, maxWidth: 200 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, lineHeight: 1 }}>
                                    Year of the export
                                </Typography>
                                <TextField
                                    size="small"
                                    type="number"
                                    value={year}
                                    onChange={(e) => { setYear(e.target.value); setTouched(false); }}
                                    error={showYearError}
                                    helperText={showYearError ? "Enter a valid year between 2000 and 3000" : undefined}
                                    disabled={isLoading}
                                    slotProps={{ htmlInput: { min: 2000, max: 3000, step: 1 } }}
                                />
                            </div>

                            <div
                                role="button"
                                tabIndex={0}
                                aria-label="Select a CSV file to import"
                                onClick={() => fileInputRef.current?.click()}
                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click(); }}
                                onDragOver={(e) => { e.preventDefault(); setIsDragActive(true); }}
                                onDragLeave={() => setIsDragActive(false)}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    setIsDragActive(false);
                                    acceptFile(e.dataTransfer.files?.[0]);
                                }}
                                style={{
                                    border: `2px dashed ${isDragActive ? ACCENT : showFileError ? "#d32f2f" : `${ACCENT}55`}`,
                                    borderRadius: 12,
                                    background: isDragActive ? `${ACCENT}0d` : "rgba(0,0,0,0.015)",
                                    padding: "28px 16px",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: 8,
                                    cursor: "pointer",
                                    transition: "border-color 160ms ease, background 160ms ease",
                                    outline: "none"
                                }}
                            >
                                <input
                                    ref={fileInputRef}
                                    hidden
                                    type="file"
                                    accept=".csv,text/csv"
                                    onChange={(e) => acceptFile(e.target.files?.[0])}
                                />

                                <div
                                    style={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: 14,
                                        background: `${ACCENT}14`,
                                        border: `1px solid ${ACCENT}33`,
                                        display: "grid",
                                        placeItems: "center",
                                        color: ACCENT
                                    }}
                                >
                                    <UploadFileRoundedIcon />
                                </div>

                                <Typography variant="body2" sx={{ fontWeight: 700, color: "#1a1a1a" }}>
                                    Drag & drop your CSV here
                                </Typography>
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                    or click to browse your files
                                </Typography>
                            </div>

                            {showFileError ? (
                                <Typography variant="caption" sx={{ color: "#d32f2f", lineHeight: 1.2, mt: -1 }}>
                                    Select a CSV file before importing
                                </Typography>
                            ) : null}

                            {file ? (
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 10,
                                        padding: "8px 12px",
                                        borderRadius: 10,
                                        background: "rgba(0,0,0,0.025)",
                                        border: `1px solid ${BORDER}`
                                    }}
                                >
                                    <InsertDriveFileOutlinedIcon sx={{ fontSize: 20, color: ACCENT }} />
                                    <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2, minWidth: 0, flex: 1 }}>
                                        <span style={{ fontSize: 13, fontWeight: 600, color: "#1a1a1a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            {file.name}
                                        </span>
                                        <span style={{ fontSize: 11, color: "rgba(0,0,0,0.5)" }}>{formatFileSize(file.size)}</span>
                                    </div>
                                    <Button
                                        size="small"
                                        onClick={resetForm}
                                        aria-label="Remove selected file"
                                        disabled={isLoading}
                                        sx={{ minWidth: 32, color: "rgba(0,0,0,0.5)" }}
                                    >
                                        <CloseRoundedIcon sx={{ fontSize: 18 }} />
                                    </Button>
                                </div>
                            ) : null}

                            {errorMessage ? (
                                <div
                                    role="alert"
                                    style={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: 8,
                                        padding: "10px 12px",
                                        borderRadius: 10,
                                        background: "#fef2f2",
                                        border: "1px solid #fecaca",
                                        color: "#b91c1c"
                                    }}
                                >
                                    <ErrorOutlineRoundedIcon sx={{ fontSize: 18, mt: "1px" }} />
                                    <Typography variant="body2" sx={{ lineHeight: 1.4 }}>{errorMessage}</Typography>
                                </div>
                            ) : null}

                            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                <Button
                                    variant="contained"
                                    onClick={onImport}
                                    disabled={!canSubmit}
                                    sx={{
                                        textTransform: "none",
                                        fontWeight: 700,
                                        borderRadius: 999,
                                        px: 3,
                                        height: 40,
                                        backgroundColor: ACCENT,
                                        "&:hover": { backgroundColor: "#b78445" },
                                        boxShadow: "none"
                                    }}
                                >
                                    Import
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
